import React, { useMemo } from 'react';
import * as XLSX from 'xlsx';
import '../../styles/css/ReportTable.css';

// Helper: format number with russian locale
const formatNumber = (num) => {
  if (!Number.isFinite(num)) return String(num);
  return new Intl.NumberFormat('ru-RU').format(num);
};

// Helper: parse CSV row with quoted values
const parseCSVRow = (row, delimiter) => {
  if (!row) return [];
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < row.length; i++) {
    const char = row[i];
    const nextChar = row[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
};

// Helper: Check for system column names
const isSystemNamesRow = (row) => {
  if (!row || row.length === 0) return false;
  const hasTabs = row.includes('\t');
  const delimiter = hasTabs ? '\t' : ';';
  const firstRow = parseCSVRow(row, delimiter).map(h => h.replace(/^"|"$/g, '').trim());
  return firstRow.some(header => {
    const h = header.trim();
    return (
      /^(mini_chart|turn-|offtake-|id-turn-|id-offtake-)/i.test(h) ||
      /^[a-z_]+-[a-z_]+(-[a-z_]+)*$/i.test(h) ||
      /^id-[a-z_-]+$/i.test(h) ||
      /^(scu|distributor|tt|id_ta)$/i.test(h)
    );
  });
};

// Helper: Remove system names row from CSV data (for downloads)
const removeSystemNamesRow = (csvData) => {
  const rows = csvData.split(/\r?\n/).filter(row => row.trim() !== '');
  if (rows.length === 0) return csvData;
  if (isSystemNamesRow(rows[0])) {
    return rows.slice(1).join('\r\n');
  }
  return csvData;
};

// Helper: format individual cell values
const formatCell = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'number') {
    return Number.isFinite(val) ? formatNumber(val) : String(val);
  }
  const s = String(val).replace(/"/g, '').trim();
  if (s === '') return '';

  const normalized = s.replace(/\s/g, '').replace(',', '.');
  const n = Number(normalized);
  if (!isNaN(n) && normalized.length > 0 && /^-?\d+(\.\d+)?$/.test(normalized)) {
    return formatNumber(n);
  }
  
  return s;
};

const TT_COLUMN_CANDIDATES_LOCAL = [
  "ТТ", "tt", "Торговая точка", "Торговая Точка", "Point of Sale", "Retail Outlet", "Название ТТ", "Outlet Name",
  "Торговый агент", "Торговий агент", "Sales Agent", "Продукт", "Product", "Дистрибьютор", "Distributor", "Область", "Region"
];

const SYS_TO_HUMAN = {
  ru: {
    'tt': 'Название ТТ',
    'tt-tt': 'Название ТТ',
    'tt-name': 'Название ТТ',
    'tt-addr': 'Адрес ТТ',
    'tt-city': 'Город',
    'region': 'Область',
    'region-region': 'Область',
    'region-name': 'Область',
    'distributor': 'Дистрибьютор',
    'distributor-group_name': 'Дистрибьютор',
    'distributor-distributor': 'Дистрибьютор',
    'scu': 'Продукт',
    'scu-scu': 'Продукт',
    'scu-name': 'Продукт',
    'scu-brend': 'Бренд',
    'ta': 'Торговый агент',
    'id_ta': 'Торговый агент',
    'id_ta-name': 'Торговый агент',
    'id_ta-id_ta': 'Торговый агент',
    'id-ta': 'Торговый агент',
    'ta-name': 'Торговый агент',
    'ta-ta': 'Торговый агент',
    'day': 'День',
    'day-day': 'День',
    'month': 'Месяц',
    'month-month': 'Месяц',
    'year': 'Год',
    'year-year': 'Год',
    'turn-cnt': 'Оборот (шт)',
    'turn-weight': 'Оборот (кг)',
    'turn-package': 'Оборот (ящ)',
    'turn-money': 'Оборот (деньги)',
    'turn-cnt-growth': 'Прирост (шт)',
    'turn-weight-growth': 'Прирост (кг)',
    'turn-package-growth': 'Прирост (ящ)',
    'turn-money-growth': 'Прирост (деньги)',
    'growth': 'Прирост',
  },
  uk: {
    'tt': 'Назва ТТ',
    'tt-tt': 'Назва ТТ',
    'tt-name': 'Назва ТТ',
    'tt-addr': 'Адреса ТТ',
    'tt-city': 'Місто',
    'region': 'Область',
    'region-region': 'Область',
    'region-name': 'Область',
    'distributor': 'Дистриб\'ютор',
    'distributor-group_name': 'Дистриб\'ютор',
    'distributor-distributor': 'Дистриб\'ютор',
    'scu': 'Продукт',
    'scu-scu': 'Продукт',
    'scu-name': 'Продукт',
    'scu-brend': 'Бренд',
    'ta': 'Торговий агент',
    'id_ta': 'Торговий агент',
    'id_ta-name': 'Торговий агент',
    'id_ta-id_ta': 'Торговий агент',
    'day': 'День',
    'day-day': 'День',
    'month': 'Місяць',
    'month-month': 'Місяць',
    'year': 'Рік',
    'year-year': 'Рік',
    'turn-cnt': 'Оборот (шт)',
    'turn-weight': 'Оборот (кг)',
    'turn-package': 'Оборот (ящ)',
    'turn-money': 'Оборот (гроші)',
    'turn-cnt-growth': 'Приріст (шт)',
    'turn-weight-growth': 'Приріст (кг)',
    'turn-package-growth': 'Приріст (ящ)',
    'turn-money-growth': 'Приріст (гроші)',
    'growth': 'Приріст',
  },
  en: {
    'tt': 'Outlet Name',
    'tt-tt': 'Outlet Name',
    'tt-name': 'Outlet Name',
    'tt-addr': 'Outlet Address',
    'tt-city': 'City',
    'region': 'Region',
    'region-region': 'Region',
    'region-name': 'Region',
    'distributor': 'Distributor',
    'distributor-group_name': 'Distributor',
    'distributor-distributor': 'Distributor',
    'scu': 'Product',
    'scu-scu': 'Product',
    'scu-name': 'Product',
    'scu-brend': 'Brand',
    'ta': 'Sales Agent',
    'id_ta': 'Sales Agent',
    'id_ta-name': 'Sales Agent',
    'id_ta-id_ta': 'Sales Agent',
    'day': 'Day',
    'day-day': 'Day',
    'month': 'Month',
    'month-month': 'Month',
    'year': 'Year',
    'year-year': 'Year',
    'turn-cnt': 'Sales (units)',
    'turn-weight': 'Sales (kg)',
    'turn-package': 'Sales (boxes)',
    'turn-money': 'Sales (money)',
    'turn-cnt-growth': 'Growth (units)',
    'turn-weight-growth': 'Growth (kg)',
    'turn-package-growth': 'Growth (boxes)',
    'turn-money-growth': 'Growth (money)',
    'growth': 'Growth',
  }
};

const MAX_DISPLAY_ROWS = 50;

const ReportTable = ({ data, language }) => {
  const text = useMemo(() => (data || '').trim(), [data]);

  const { initialHeaders, initialDataRows, totalRowCount } = useMemo(() => {
    let headers = [];
    let dataRows = [];
    let totalRowCount = 0;

    if (text.startsWith('[') || text.startsWith('{')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && !Array.isArray(parsed[0])) {
          headers = Object.keys(parsed[0]);
          totalRowCount = parsed.length;
          dataRows = parsed.slice(0, MAX_DISPLAY_ROWS).map((obj, index) => ({
            index,
            columns: headers.map(h => obj[h])
          }));
        }
      } catch (e) {}
    }

    if (headers.length === 0 || dataRows.length === 0) {
      const rows = text.split(/\r?\n/).filter(row => row.trim() !== '');
      if (rows.length > 0) {
        // Detect delimiter: check multiple rows to determine the most common delimiter
        const sampleRowsForDelim = rows.slice(0, Math.min(5, rows.length));
        let tabCount = 0;
        let semicolonCount = 0;
        sampleRowsForDelim.forEach(row => {
          tabCount += (row.match(/\t/g) || []).length;
          semicolonCount += (row.match(/;/g) || []).length;
        });
        const delimiter = tabCount > semicolonCount ? '\t' : ';';

        const hasSystemNames = isSystemNamesRow(rows[0]);
        const sysNamesRow = hasSystemNames ? parseCSVRow(rows[0], delimiter).map(s => s.replace(/^"|"$/g, '').trim()) : [];
        let headerRowIndex = hasSystemNames ? 1 : 0;
        
        // Function to strictly check if a row is an informational "interval" row
        const checkIsInterval = (cells) => {
          if (!cells || cells.length === 0) return false;
          
          const firstCell = cells[0]?.replace(/^"|"$/g, '').trim() || '';
          const hasKeyword = /интервал|interval|інтервал/i.test(firstCell);
          
          // If first cell has keyword, it's definitely an interval row
          if (hasKeyword) {
            // Check if other cells contain date ranges (YYYY-MM-DD - YYYY-MM-DD) or growth keywords
            const hasDateRanges = cells.slice(1).some(cell => {
              const cleanCell = String(cell || '').replace(/^"|"$/g, '').trim();
              return /^\d{4}-\d{2}-\d{2}\s*-\s*\d{4}-\d{2}-\d{2}$/.test(cleanCell);
            });
            const hasGrowthKeywords = cells.slice(1).some(cell => {
              const cleanCell = String(cell || '').replace(/^"|"$/g, '').trim().toLowerCase();
              return cleanCell === 'growth' || cleanCell === 'прирост' || cleanCell === 'зростання';
            });
            return hasDateRanges || hasGrowthKeywords || cells.length > 2;
          }
          
          // If no keyword, it's not an interval row (even if it has dates - those are data rows)
          return false;
        };
        
        // Check the current row that we want to use as headers
        let potentialHeader = parseCSVRow(rows[headerRowIndex], delimiter);
        let intervalDates = null; // Store dates from interval row if found
        let intervalGrowthKeywords = null; // Store growth keywords from interval row if found
        
        // IF this is an interval row, extract dates and growth keywords, then SKIP to the next one
        if (checkIsInterval(potentialHeader) && headerRowIndex + 1 < rows.length) {
          // Extract dates and growth keywords from interval row (skip first column which is "Интервал дат")
          intervalDates = potentialHeader.slice(1).map(cell => {
            const cleanCell = cell.replace(/^"|"$/g, '').trim();
            // Return date range if it matches pattern, otherwise return null
            return /^\d{4}-\d{2}-\d{2}\s*-\s*\d{4}-\d{2}-\d{2}$/.test(cleanCell) ? cleanCell : null;
          });
          
          // Extract growth keywords (Прирост, Growth, Зростання) from interval row
          intervalGrowthKeywords = potentialHeader.slice(1).map(cell => {
            const cleanCell = cell.replace(/^"|"$/g, '').trim().toLowerCase();
            if (cleanCell === 'growth' || cleanCell === 'прирост' || cleanCell === 'зростання') {
              return cleanCell;
            }
            return null;
          });
          
          headerRowIndex++;
        }
        
        const actualHeaderRow = parseCSVRow(rows[headerRowIndex], delimiter);
        
        // Heuristic: Check if the header row looks like data instead of headers
        const looksLikeData = actualHeaderRow.some(cell => {
          const s = cell.replace(/\s/g, '').replace(',', '.');
          const n = Number(s);
          // If it's a number and not a common year/ID (like 2024, 2025)
          return !isNaN(n) && s.length > 0 && (n === 0 || s.includes('.') || n > 2100);
        });

        let dataStartIndex;
        if (looksLikeData && !hasSystemNames) {
          // If it looks like data, generate dummy headers and start data from this row
          headers = actualHeaderRow.map((_, i) => `Колонка ${i + 1}`);
          dataStartIndex = headerRowIndex;
        } else {
          // Normal case: use header row as headers
          const lang = language || 'ru';
          const mapping = SYS_TO_HUMAN[lang] || SYS_TO_HUMAN.ru;
          
          headers = actualHeaderRow.map((h, i) => {
            const cleanH = h.replace(/^"|"$/g, '').trim();
            const sys = sysNamesRow[i] ? sysNamesRow[i].toLowerCase() : null;
            
            // Skip mini_chart columns - they will be filtered later
            const isMiniChart = sys && (sys === 'mini_chart' || sys === 'mini-chart' || 
                                       sys === 'mini_chart-caption' || sys === 'mini-chart-caption' ||
                                       sys.includes('mini_chart') || sys.includes('mini-chart'));
            const isMiniChartHeader = cleanH.toLowerCase().includes('мини график') || 
                                     cleanH.toLowerCase().includes('mini chart');
            
            if (isMiniChart || isMiniChartHeader) {
              // Return special marker - this column will be filtered out later
              // Use a unique marker that won't match anything else
              return '__MINI_CHART_TO_FILTER__';
            }
            
            // First, determine the base header name (e.g., "Оборот (шт)", "Дистрибьютор", etc.)
            let baseHeader = cleanH;
            const looksLikePeriod = cleanH === 'Интервал дат' || /^\d{4}-\d{2}-\d{2}/.test(cleanH);
            
            if ((!cleanH || looksLikePeriod) && sys) {
              const cleanSys = sys.replace(/^id-/i, '');
              const mapped = mapping[sys] || mapping[cleanSys] || mapping[sys.split('-')[0]];
              if (mapped) {
                if (cleanH === 'Интервал дат') baseHeader = mapped;
                else if (/^\d{4}-\d{2}-\d{2}/.test(cleanH)) baseHeader = cleanH;
                else baseHeader = mapped;
              }
            }

            // Next, if we have interval dates, we want to PREPEND them with a newline
            if (intervalDates && i > 0 && i - 1 < intervalDates.length && intervalDates[i - 1]) {
              return `${intervalDates[i - 1]}\n${baseHeader}`;
            }
            
            // If we have growth keywords, prepend them
            if (intervalGrowthKeywords && i > 0 && i - 1 < intervalGrowthKeywords.length && intervalGrowthKeywords[i - 1]) {
              const growthKeyword = intervalGrowthKeywords[i - 1];
              if (cleanH.includes('%') || baseHeader.includes('%')) {
                const growthLabels = {
                  'ru': { 'growth': 'Прирост', 'прирост': 'Прирост', 'зростання': 'Приріст' },
                  'uk': { 'growth': 'Приріст', 'прирост': 'Приріст', 'зростання': 'Приріст' },
                  'en': { 'growth': 'Growth', 'прирост': 'Growth', 'зростання': 'Growth' }
                };
                const langKey = language || 'ru';
                const growthLabel = growthLabels[langKey]?.[growthKeyword] || growthLabels['ru'][growthKeyword] || 'Прирост';
                const cleanedBaseHeader = baseHeader.replace(/,?\s*%/, '').trim();
                return cleanedBaseHeader ? `${growthLabel}, %\n${cleanedBaseHeader}` : `${growthLabel}, %`;
              }
            }
            
            return baseHeader;
          });
          dataStartIndex = headerRowIndex + 1;
        }

        // Find where data actually starts by skipping all header-like rows or duplicates
        while (dataStartIndex < rows.length) {
          const cells = parseCSVRow(rows[dataStartIndex], delimiter);
          
          // Skip interval rows
          if (checkIsInterval(cells)) {
            dataStartIndex++;
            continue;
          }
          
          // Check if first column is a date (YYYY-MM-DD format) - this is definitely data
          const firstCell = cells[0]?.replace(/^"|"$/g, '').trim() || '';
          const isDateInFirstColumn = /^\d{4}-\d{2}-\d{2}$/.test(firstCell) || /^\d{4}-\d{2}$/.test(firstCell) || /^\d{2}\/\d{4}$/.test(firstCell) || /^\d{4}$/.test(firstCell);
          
          if (isDateInFirstColumn && cells.length > 1) {
            // If first column is a date, check if there are numbers in other columns
            const hasNumbers = cells.slice(1).some(c => {
              const s = String(c || '').replace(/^"|"$/g, '').replace(/\s/g, '').replace(',', '.');
              const n = Number(s);
              return !isNaN(n) && s.length > 0 && /^-?\d+(\.\d+)?$/.test(s);
            });
            if (hasNumbers) {
              // This is definitely a data row with date + numbers
              break;
            }
          }
          
          // Check if this row looks like data
          // For date-based reports: first column might be a date (YYYY-MM-DD), other columns should be numbers
          // For other reports: should have at least one numeric value
          const looksLikeDataRow = cells.some((cell, idx) => {
            const trimC = cell.trim();
            // Check if it's a date in YYYY-MM-DD format
            const isDate = /^\d{4}-\d{2}-\d{2}$/.test(trimC) || /^\d{4}-\d{2}$/.test(trimC) || /^\d{2}\/\d{4}$/.test(trimC) || /^\d{4}$/.test(trimC);
            if (isDate && idx === 0 && cells.length > 1) {
              // If first column is a date, check if there are numbers in other columns
              const hasNumbers = cells.slice(1).some(c => {
                const s = String(c || '').replace(/\s/g, '').replace(',', '.');
                const n = Number(s);
                return !isNaN(n) && s.length > 0 && /^-?\d+(\.\d+)?$/.test(s);
              });
              return hasNumbers; // It's data if date + numbers
            }
            // Check if it's a number
            const s = trimC.replace(/\s/g, '').replace(',', '.');
            const n = Number(s);
            // It's data if it's a number (including 0, decimals, or large numbers)
            return !isNaN(n) && s.length > 0 && /^-?\d+(\.\d+)?$/.test(s);
          });
          
          // Check if this row looks like a header
          // A row is a header if it contains header keywords AND doesn't have numbers
          const isHeaderLike = cells.some(cell => {
            const trimC = cell.trim();
            return trimC.includes('Интервал дат') || 
                   TT_COLUMN_CANDIDATES_LOCAL.some(cand => trimC === cand);
          }) && !looksLikeDataRow; // Only header if no data found

          if (looksLikeDataRow && !isHeaderLike) break;
          dataStartIndex++;
        }
        
        const parsedRows = rows.slice(dataStartIndex)
          .map((row, index) => ({
            index,
            columns: parseCSVRow(row, delimiter).map(col => col.replace(/^"|"$/g, '').trim())
          }))
          .filter(rowData => {
            // Filter out interval rows
            if (checkIsInterval(rowData.columns)) {
              return false;
            }

            const isHeader = rowData.columns.some((cell, idx) => {
              const trimC = cell.trim();
              // Check for specific header keywords (but not dates)
              if (trimC.includes('Интервал дат') || 
                  TT_COLUMN_CANDIDATES_LOCAL.some(cand => trimC === cand)) {
                // But if this row has a date in first column and numbers, it's data, not header
                if (idx === 0 && (/^\d{4}-\d{2}-\d{2}$/.test(trimC) || /^\d{4}-\d{2}$/.test(trimC) || /^\d{2}\/\d{4}$/.test(trimC) || /^\d{4}$/.test(trimC)) && rowData.columns.length > 1) {
                  // Check if there are numbers in other columns
                  const hasNumbers = rowData.columns.slice(1).some(c => {
                    const s = String(c || '').replace(/\s/g, '').replace(',', '.');
                    const n = Number(s);
                    return !isNaN(n) && s.length > 0 && /^-?\d+(\.\d+)?$/.test(s);
                  });
                  return !hasNumbers; // It's a header only if no numbers found
                }
                return true;
              }
              return false;
            });
            return !isHeader;
          });

        // Count real data rows (interval rows already dropped)
        totalRowCount = parsedRows.length;
        dataRows = parsedRows.slice(0, MAX_DISPLAY_ROWS);
      }
    }
    return { initialHeaders: headers, initialDataRows: dataRows, totalRowCount };
  }, [text, language]);

  const isTruncated = totalRowCount > MAX_DISPLAY_ROWS;

  const { headers, dataRows } = useMemo(() => {
    // Get sysNames for filtering
    const firstLine = text.split(/\r?\n/)[0] || '';
    const hasSystemNames = isSystemNamesRow(firstLine);
    let sysNamesForFilter = [];
    if (hasSystemNames) {
      const lines = text.split(/\r?\n/).filter(l => l.trim() !== '').slice(0, 5);
      let tCount = 0;
      let sCount = 0;
      lines.forEach(l => {
        tCount += (l.match(/\t/g) || []).length;
        sCount += (l.match(/;/g) || []).length;
      });
      const delimiter = tCount > sCount ? '\t' : ';';
      sysNamesForFilter = parseCSVRow(firstLine, delimiter).map(s => s.replace(/^"|"$/g, '').trim());
    }
    
    const columnsToHide = initialHeaders.reduce((acc, header, idx) => {
      const h = header.toLowerCase().replace(/"/g, '').replace(/[_-]/g, ' ').trim();
      
      // Check for special marker for mini_chart
      if (header === '__MINI_CHART_TO_FILTER__') {
        acc.add(idx);
        return acc;
      }
      
      // Check header text
      if (h === 'мини график' || h === 'mini chart') {
        acc.add(idx);
        return acc;
      }

      if (!header || header.trim() === '') {
        // Check sysNames to confirm it's mini_chart
        if (sysNamesForFilter.length > idx) {
          const sysName = sysNamesForFilter[idx]?.toLowerCase().trim() || '';
          if (sysName === 'mini_chart' || sysName === 'mini-chart' || 
              sysName === 'mini_chart-caption' || sysName === 'mini-chart-caption' ||
              sysName.includes('mini_chart') || sysName.includes('mini-chart')) {
            acc.add(idx);
            return acc;
          }
        }
      }

      if (sysNamesForFilter.length > idx) {
        const sysName = sysNamesForFilter[idx]?.toLowerCase().trim() || '';
        if (sysName === 'mini_chart' || sysName === 'mini-chart' || 
            sysName === 'mini_chart-caption' || sysName === 'mini-chart-caption' ||
            sysName.includes('mini_chart') || sysName.includes('mini-chart')) {
          acc.add(idx);
          return acc;
        }
      }
      
      return acc;
    }, new Set());

    if (columnsToHide.size > 0) {
      return {
        headers: initialHeaders.filter((_, idx) => !columnsToHide.has(idx)),
        dataRows: initialDataRows.map(row => ({
          ...row,
          columns: row.columns.filter((_, idx) => !columnsToHide.has(idx))
        }))
      };
    }
    return { headers: initialHeaders, dataRows: initialDataRows };
  }, [initialHeaders, initialDataRows, text]);

  const sysNames = useMemo(() => {
    const firstLine = text.split(/\r?\n/)[0] || '';
    if (!isSystemNamesRow(firstLine)) return [];
    
    const lines = text.split(/\r?\n/).filter(l => l.trim() !== '').slice(0, 5);
    let tCount = 0;
    let sCount = 0;
    lines.forEach(l => {
      tCount += (l.match(/\t/g) || []).length;
      sCount += (l.match(/;/g) || []).length;
    });
    const delimiter = tCount > sCount ? '\t' : ';';
    const allSysNames = parseCSVRow(firstLine, delimiter).map(s => s.replace(/^"|"$/g, '').trim());

    const columnsToHide = initialHeaders.reduce((acc, header, idx) => {
      const h = header.toLowerCase().replace(/"/g, '').replace(/[_-]/g, ' ').trim();
      
      // Check for special marker for mini_chart
      if (header === '__MINI_CHART_TO_FILTER__') {
        acc.add(idx);
        return acc;
      }

      if (h === 'мини график' || h === 'mini chart') {
        acc.add(idx);
        return acc;
      }
      
      if (!header || header.trim() === '') {
        // Check sysNames to confirm it's mini_chart
        if (allSysNames.length > idx) {
          const sysName = allSysNames[idx]?.toLowerCase().trim() || '';
          if (sysName === 'mini_chart' || sysName === 'mini-chart' || 
              sysName === 'mini_chart-caption' || sysName === 'mini-chart-caption' ||
              sysName.includes('mini_chart') || sysName.includes('mini-chart')) {
            acc.add(idx);
            return acc;
          }
        }
      }
      
      // Check sysNames for mini_chart
      if (allSysNames.length > idx) {
        const sysName = allSysNames[idx]?.toLowerCase().trim() || '';
        if (sysName === 'mini_chart' || sysName === 'mini-chart' || 
            sysName === 'mini_chart-caption' || sysName === 'mini-chart-caption' ||
            sysName.includes('mini_chart') || sysName.includes('mini-chart')) {
          acc.add(idx);
          return acc;
        }
      }
      
      return acc;
    }, new Set());
    
    // Return filtered sysNames to match filtered headers/dataRows
    return allSysNames.filter((_, idx) => !columnsToHide.has(idx));
  }, [text, initialHeaders]);

  const tableRows = useMemo(() => {
    return dataRows.map((rowData) => {
      const rowIdColIdx = sysNames.findIndex(sn => /^id-(tt|distributor|scu)$/i.test(sn));
      const nameColIdx = sysNames.findIndex(sn => /^(tt|distributor|scu)-(tt|name|scu)$/i.test(sn));
      
      let rowId = '';
      if (rowIdColIdx >= 0) {
        rowId = String(rowData.columns[rowIdColIdx] || '').trim();
      }
      
      if (!rowId && nameColIdx >= 0) {
        const nameVal = String(rowData.columns[nameColIdx] || '').replace(/"/g, '').trim();
        const idMatch = nameVal.match(/^([\d-]+)\s+(.+)$/);
        if (idMatch) {
          rowId = idMatch[1];
        } else {
          const idMatchNoSpace = nameVal.match(/^([\d-]+)([^\d].*)$/);
          if (idMatchNoSpace) {
            rowId = idMatchNoSpace[1];
          }
        }
      }

      return (
        <tr key={rowData.index}>
          {rowData.columns.map((col, idx) => {
            const sn = sysNames[idx] || '';
            if (/^id-(tt|distributor|scu)$/i.test(sn)) return null;

            let cellValue = col;
            const isName = /^(tt|distributor|scu)-(tt|name|scu)$/i.test(sn);
            const isAddr = /^(tt|distributor|scu)-addr$/i.test(sn);
            
            if (isName || isAddr) {
              let s = String(col).replace(/"/g, '').trim();
              if (rowId) {
                const idPrefix = `(${rowId})`;
                if (isName) {
                  if (s.startsWith(`${rowId} `)) {
                    cellValue = `${idPrefix}${s.substring(rowId.length).trim()}`;
                  } else if (s.startsWith(`${rowId}`) && s.length > rowId.length) {
                    cellValue = `${idPrefix}${s.substring(rowId.length)}`;
                  } else if (s === rowId) {
                    cellValue = idPrefix;
                  } else if (!s.startsWith(idPrefix)) {
                    cellValue = `${idPrefix}${s}`;
                  } else {
                    cellValue = s;
                  }
                } else if (isAddr) {
                  const addrTrimmed = s.trim();
                  if (addrTrimmed && addrTrimmed !== '') {
                    if (!addrTrimmed.startsWith(idPrefix)) {
                      cellValue = `${idPrefix}${addrTrimmed}`;
                    } else {
                      cellValue = addrTrimmed;
                    }
                  } else {
                    cellValue = '';
                  }
                }
              } else {
                cellValue = s;
              }
            }

            return (
              <td key={idx} className="report-table-cell">
                {(isName || isAddr) ? cellValue : formatCell(cellValue)}
              </td>
            );
          })}
        </tr>
      );
    });
  }, [dataRows, sysNames]);

  const downloadCSV = () => {
    const cleanedData = removeSystemNamesRow(data || '');
    const hasTabs = cleanedData.includes('\t');
    const delimiter = hasTabs ? '\t' : ';';
    const rows = cleanedData.split(/\r?\n/).filter(r => r.trim() !== '');
    if (rows.length === 0) return;

    const firstRow = parseCSVRow(rows[0], delimiter);
    const columnsToHide = firstRow.reduce((acc, header, idx) => {
      const h = header.toLowerCase().replace(/"/g, '').replace(/[_-]/g, ' ').trim();
      if (h === 'мини график' || h === 'mini chart') {
        acc.add(idx);
      }
      return acc;
    }, new Set());

    const processedRows = rows.map(row => {
      const cells = parseCSVRow(row, delimiter);
      return cells.filter((_, idx) => !columnsToHide.has(idx)).join(';');
    });

    const fullData = processedRows.join('\r\n');
    const blob = new Blob(['\uFEFF', fullData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'report.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const downloadExcel = () => {
    const cleanedData = removeSystemNamesRow(data || '');
    const hasTabs = cleanedData.includes('\t');
    const delimiter = hasTabs ? '\t' : ';';
    const rows = cleanedData.split(/\r?\n/).filter(r => r.trim() !== '');
    if (rows.length === 0) return;

    const firstRow = parseCSVRow(rows[0], delimiter);
    const columnsToHide = firstRow.reduce((acc, header, idx) => {
      const h = header.toLowerCase().replace(/"/g, '').replace(/[_-]/g, ' ').trim();
      if (h === 'мини график' || h === 'mini chart') {
        acc.add(idx);
      }
      return acc;
    }, new Set());

    const fullRows = rows.map(r =>
      parseCSVRow(r, delimiter)
        .filter((_, idx) => !columnsToHide.has(idx))
        .map(c => c.replace(/^"|"$/g, '').replace(/""/g, ''))
    );

    const updatedRows = fullRows.map(row =>
      row.map(cell => {
        const normalized = String(cell).replace(/\s/g, '').replace(',', '.');
        const num = Number(normalized);
        if (!isNaN(num) && normalized.length > 0 && /^-?\d+(\.\d+)?$/.test(normalized)) {
          return num;
        }
        return cell;
      })
    );

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(updatedRows);
    XLSX.utils.book_append_sheet(wb, ws, 'Report');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob(
      [wbout],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'report.xlsx';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const showDownloadButtons = useMemo(() => (data || '').split(/\r?\n/).length > 5, [data]);

  const buttonTexts = {
    ru: { downloadCSV: "Скачать полную таблицу как CSV", downloadExcel: "Скачать полную таблицу как Excel (XLSX)" },
    uk: { downloadCSV: "Завантажити повну таблицю як CSV", downloadExcel: "Завантажити повну таблицю як Excel (XLSX)" },
    en: { downloadCSV: "Download Full Table as CSV", downloadExcel: "Download Full Table as Excel (XLSX)" }
  };

  const truncationNoticeTexts = {
    ru: (total) => `⚠️ Таблица отображает только первые ${MAX_DISPLAY_ROWS} строк из ${total}. Полный список можно выгрузить в Excel или CSV, воспользовавшись кнопками ниже.`,
    uk: (total) => `⚠️ Таблиця відображає лише перші ${MAX_DISPLAY_ROWS} рядків з ${total}. Повний список можна завантажити в Excel або CSV, скориставшись кнопками нижче.`,
    en: (total) => `⚠️ The table shows only the first ${MAX_DISPLAY_ROWS} rows out of ${total}. You can download the full list as Excel or CSV using the buttons below.`,
  };

  return (
    <div className="report-table-container">
      {isTruncated && (
        <div className="report-table-truncation-notice">
          {(truncationNoticeTexts[language] || truncationNoticeTexts.en)(totalRowCount)}
        </div>
      )}
      <table className="report-table">
        <thead>
          <tr>
            {headers.map((header, idx) => {
              const sn = sysNames[idx] || '';
              if (/^id-(tt|distributor|scu)$/i.test(sn)) return null;
              return (
                <th key={idx} className="report-table-header">
                  {header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{tableRows}</tbody>
      </table>

      {showDownloadButtons && (
        <div className="report-table-buttons-container">
          <button onClick={downloadCSV} className="report-table-button report-table-button-csv">
            {buttonTexts[language]?.downloadCSV || buttonTexts.en.downloadCSV}
          </button>
          <button onClick={downloadExcel} className="report-table-button report-table-button-excel">
            {buttonTexts[language]?.downloadExcel || buttonTexts.en.downloadExcel}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportTable;
